import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
    getAllActiveLoanEligibilityConfigs,
    getAllLoanEligibilityConfigs,
    getLoanEligibilityConfigById,
    getLoanEligibilityConfigByName,
    getApplicableLoanEligibilityConfig,
    createLoanEligibilityConfig,
    updateLoanEligibilityConfig,
    deleteLoanEligibilityConfig,
    toggleLoanEligibilityConfigActive,
    evictLoanEligibilityConfigCache,
    calculateLoanEligibleAmount
} from '@/shared/api/client/loan-eligibility-configuration';
import { LoanEligibilityConfigDTO } from '@/shared/api/model';
import { showErrorNotification, showSuccessNotification } from '@/components/common/utils/notify';

/**
 * Hook to get all active loan eligibility configurations
 */
export const useActiveLoanEligibilityConfigs = () => {
    return useQuery({
        queryKey: ['loan-eligibility-config', 'active'],
        queryFn: async () => {
            const response = await getAllActiveLoanEligibilityConfigs();
            return response;
        }
    });
};

/**
 * Hook to get all loan eligibility configurations (active and inactive)
 */
export const useAllLoanEligibilityConfigs = () => {
    return useQuery({
        queryKey: ['loan-eligibility-config', 'all'],
        queryFn: async () => {
            const response = await getAllLoanEligibilityConfigs();
            return response;
        }
    });
};

/**
 * Hook to get a single loan eligibility configuration by ID
 */
export const useLoanEligibilityConfigById = (id: number | undefined) => {
    return useQuery({
        queryKey: ['loan-eligibility-config', id],
        queryFn: async () => {
            const response = await getLoanEligibilityConfigById(id as number);
            return response;
        },
        enabled: !!id
    });
};

/**
 * Hook to get a loan eligibility configuration by name
 */
export const useLoanEligibilityConfigByName = (configName: string | undefined) => {
    return useQuery({
        queryKey: ['loan-eligibility-config', 'name', configName],
        queryFn: async () => {
            const response = await getLoanEligibilityConfigByName(configName as string);
            return response;
        },
        enabled: !!configName
    });
};

/**
 * Hook to get applicable configuration for a branch and product
 */
export const useApplicableConfig = (branch: string | undefined, glNo: string | undefined) => {
    return useQuery({
        queryKey: ['loan-eligibility-config', 'applicable', branch, glNo],
        queryFn: async () => {
            const response = await getApplicableLoanEligibilityConfig({ branch: branch!, glNo: glNo! });
            return response;
        },
        enabled: !!branch && !!glNo
    });
};

/**
 * Hook to create a new loan eligibility configuration
 */
export const useCreateLoanEligibilityConfig = () => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: async (data: LoanEligibilityConfigDTO) => {
            const response = await createLoanEligibilityConfig(data);
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['loan-eligibility-config'] });
            showSuccessNotification('Loan eligibility configuration created successfully');
        },
        onError: (error: any) => {
            showErrorNotification(
                'Failed to create configuration',
                error.response?.data?.message || error.message || 'An error occurred'
            );
        }
    });
};

/**
 * Hook to update an existing loan eligibility configuration
 */
export const useUpdateLoanEligibilityConfig = () => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: async ({ id, data }: { id: number, data: LoanEligibilityConfigDTO }) => {
            const response = await updateLoanEligibilityConfig(id, data);
            return response;
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['loan-eligibility-config'] });
            queryClient.invalidateQueries({ queryKey: ['loan-eligibility-config', variables.id] });
            showSuccessNotification('Loan eligibility configuration updated successfully');
        },
        onError: (error: any) => {
            showErrorNotification(
                'Failed to update configuration',
                error.response?.data?.message || error.message || 'An error occurred'
            );
        }
    });
};

/**
 * Hook to delete a loan eligibility configuration
 */
export const useDeleteLoanEligibilityConfig = () => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: async (id: number) => {
            await deleteLoanEligibilityConfig(id);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['loan-eligibility-config'] });
            showSuccessNotification('Loan eligibility configuration deleted successfully');
        },
        onError: (error: any) => {
            showErrorNotification(
                'Failed to delete configuration',
                error.response?.data?.message || error.message || 'An error occurred'
            );
        }
    });
};

/**
 * Hook to toggle active status of a loan eligibility configuration
 */
export const useToggleLoanEligibilityConfigActive = () => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: async (id: number) => {
            const response = await toggleLoanEligibilityConfigActive(id);
            return response;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['loan-eligibility-config'] });
            queryClient.invalidateQueries({ queryKey: ['loan-eligibility-config', data.id] });
            showSuccessNotification(
                `Configuration ${data.active ? 'activated' : 'deactivated'} successfully`
            );
        },
        onError: (error: any) => {
            showErrorNotification(
                'Failed to toggle configuration status',
                error.response?.data?.message || error.message || 'An error occurred'
            );
        }
    });
};

/**
 * Hook to manually evict all caches
 */
export const useEvictLoanEligibilityConfigCache = () => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: async () => {
            await evictLoanEligibilityConfigCache();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['loan-eligibility-config'] });
            showSuccessNotification('Cache evicted successfully');
        },
        onError: (error: any) => {
            showErrorNotification(
                'Failed to evict cache',
                error.response?.data?.message || error.message || 'An error occurred'
            );
        }
    });
};

/**
 * Hook to calculate eligible amount
 */
export const useCalculateEligibleAmount = () => {
    return useMutation({
        mutationFn: async (params: {
            branch: string;
            glNo: string;
            savingsBalance: number;
            existingLoanBalance?: number;
        }) => {
            const response = await calculateLoanEligibleAmount({
                branch: params.branch,
                glNo: params.glNo,
                savingsBalance: params.savingsBalance,
                existingLoanBalance: params.existingLoanBalance || 0
            });
            return response;
        },
        onError: (error: any) => {
            showErrorNotification(
                'Failed to calculate eligible amount',
                error.response?.data?.message || error.message || 'An error occurred'
            );
        }
    });
};
